import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { ReviewForm } from '../components/ReviewForm';
import MediaGallery from '../components/MediaGallery';
import { normalizeRating } from '../utils/ratingHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [quoteData, setQuoteData] = useState({ message: '', preferred_date: '' });
  const [reportData, setReportData] = useState({ reason: '', details: '' });

  useEffect(() => {
    fetchService();
  }, [serviceId]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);
    } catch (error) {
      console.error('Failed to fetch service:', error);
      toast.error('Service not found');
      navigate('/tradies/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageTradie = async (tradieUserId) => {
    if (!user?.email_verified) {
      toast.error('Please verify your email before messaging tradies');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/conversations?other_user_id=${tradieUserId}`
      );
      navigate(`/messages?conversation=${response.data.conversation_id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const handleQuoteRequest = async () => {
    if (!user) {
      toast.error('Please login to request a quote');
      return;
    }
    try {
      await axios.post(`${API}/quotes/request`, {
        service_id: serviceId,
        tradie_id: service.tradie_id,
        message: quoteData.message,
        preferred_date: quoteData.preferred_date,
        contact_method: 'email'
      });
      toast.success('Quote request sent successfully!');
      setShowQuoteModal(false);
      setQuoteData({ message: '', preferred_date: '' });
    } catch (error) {
      toast.error('Failed to send quote request');
    }
  };

  const handleReportSubmit = async () => {
    if (!user) {
      toast.error('Please login to report');
      return;
    }
    try {
      await axios.post(`${API}/reports/submit`, {
        listing_type: 'service',
        listing_id: serviceId,
        reason: reportData.reason,
        details: reportData.details
      });
      toast.success('Report submitted. Thank you!');
      setShowReportModal(false);
      setReportData({ reason: '', details: '' });
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const getGSTBadge = (priceMode) => {
    switch (priceMode) {
      case 'gst_included':
        return <Badge variant="outline" className="text-xs">GST included</Badge>;
      case 'plus_gst':
        return <Badge variant="outline" className="text-xs">+GST</Badge>;
      case 'no_gst':
        return <Badge variant="outline" className="text-xs">No GST</Badge>;
      default:
        return null;
    }
  };

  const getVerificationBadge = (status) => {
    const badges = {
      verified: <Badge className="bg-green-500 text-white text-xs">✓ Verified</Badge>,
      pending: <Badge variant="outline" className="text-yellow-600 text-xs">⏳ Pending</Badge>,
      rejected: <Badge variant="destructive" className="text-xs">✗ Rejected</Badge>,
      not_provided: <Badge variant="secondary" className="text-xs">Not Provided</Badge>,
      not_required: <Badge variant="secondary" className="text-xs">Not Required</Badge>,
    };
    return badges[status] || badges.not_provided;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-8">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="service-detail-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/tradies/browse')}
          className="mb-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Browse</span>
        </button>

        <div className="max-w-5xl mx-auto">
          {/* Grid Layout - Image on left, Content on right */}
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            {/* Left Column - Media Gallery */}
            <div className="md:col-span-2">
              {service.tradie?.photos && service.tradie.photos.length > 0 ? (
                <div className="space-y-3">
                  {/* Main Image - Clickable */}
                  <div 
                    className="h-64 rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                    onClick={() => { setGalleryIndex(0); setShowGallery(true); }}
                  >
                    <img 
                      src={service.tradie.photos[0]} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  
                  {/* Additional Photos */}
                  {service.tradie.photos.length > 1 && (
                    <div className="grid grid-cols-3 gap-2">
                      {service.tradie.photos.slice(1, 4).map((photo, idx) => (
                        <div key={idx} className="h-20 rounded-lg overflow-hidden shadow cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => { setGalleryIndex(idx + 1); setShowGallery(true); }}>
                          <img 
                            src={photo} 
                            alt={`${service.title} ${idx + 2}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (service.tradie?.photos && service.tradie.photos.length > 0) || (service.media_urls && service.media_urls.length > 0) ? (
                <div className="space-y-3">
                  {/* Main Image - Clickable */}
                  <div 
                    className="h-64 rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                    onClick={() => { setGalleryIndex(0); setShowGallery(true); }}
                  >
                    <img 
                      src={service.tradie?.photos?.[0] || service.media_urls?.[0]} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  
                  {/* Media Gallery with Zoom */}
                  <div className="grid grid-cols-3 gap-2">
                    {(service.tradie?.photos || service.media_urls || []).slice(1, 4).map((photo, idx) => (
                      <div key={idx} className="h-20 rounded-lg overflow-hidden shadow cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => { setGalleryIndex(idx + 1); setShowGallery(true); }}>
                        <img src={photo} alt={`${service.title} ${idx + 2}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-700 dark:to-blue-800 rounded-xl flex items-center justify-center shadow-lg">
                  <div className="text-center text-white">
                    <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm opacity-75">No photos</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="md:col-span-3 space-y-6">
              {/* Title & ID */}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2 text-foreground" data-testid="service-title">{service.title}</h1>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-muted-foreground">Listing ID:</span>
                  <code className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm font-bold text-blue-600 dark:text-blue-400 tracking-wider">
                    {service.tradie?.tradie_code || service.public_ref || service.id}
                  </code>
                </div>
                <button
                  onClick={() => service.tradie && navigate(`/tradies/tradie/${service.tradie.id}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-semibold text-lg"
                  data-testid="tradie-link"
                >
                  {service.tradie?.business_name}
                </button>
              </div>

              {/* Rating & Location */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1" data-testid="service-rating">
                  <span className="text-yellow-500 text-xl">★</span>
                  <span className="font-semibold">{normalizeRating(service.rating_avg).toFixed(1)}</span>
                  <span className="text-muted-foreground">({service.rating_count} reviews)</span>
                </div>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground" data-testid="service-location">{service.suburb}, {service.state}</span>
              </div>

              {/* Price */}
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="text-sm text-muted-foreground mb-1">Starting from</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-foreground" data-testid="service-price">${service.price_base.toFixed(2)}</span>
                  {getGSTBadge(service.price_mode)}
                </div>
              </div>

              {/* Horizontal Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    if (!user) {
                      toast.error('Please login to message tradies');
                      navigate('/login');
                    } else {
                      handleMessageTradie(service.tradie.user_id);
                    }
                  }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-md"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Message
                </button>

                <button
                  onClick={() => {
                    if (!user) {
                      toast.error('Please login to request quotes');
                      navigate('/login');
                    } else {
                      setShowQuoteModal(true);
                    }
                  }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-all shadow-md"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Request Quote
                </button>

                <button
                  onClick={() => {
                    if (!user) {
                      toast.error('Please login to save listings');
                      navigate('/login');
                    } else {
                      toast.success('Saved to your favorites!');
                    }
                  }}
                  className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-border font-semibold rounded-xl hover:bg-muted transition-all text-foreground"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Save
                </button>

                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center justify-center gap-2 px-6 py-3 border border-border font-medium rounded-xl hover:bg-muted transition-all text-muted-foreground hover:text-foreground"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                  </svg>
                  Report
                </button>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-foreground">Service Details</h2>
              
              <div className="space-y-6">
                {(service.about || service.detailed_description) && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">About this service</h3>
                    <p className="text-muted-foreground">{service.about || service.detailed_description}</p>
                  </div>
                )}

                {service.inclusions && service.inclusions.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Inclusions</h3>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {service.inclusions.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {service.exclusions && service.exclusions.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Exclusions</h3>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {service.exclusions.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Separator />

                {/* Verification Status */}
                {service.tradie && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Tradie Verification</h3>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">ABN:</span>
                        {getVerificationBadge(service.tradie.abn_status || 'not_provided')}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Licence:</span>
                        {getVerificationBadge(service.tradie.licence_status || 'not_provided')}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Insurance:</span>
                        {getVerificationBadge(service.tradie.insurance_status || 'not_provided')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reviews Section */}
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Customer Reviews</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/30 px-3 py-1.5 rounded-lg">
                      <span className="text-yellow-500 text-2xl">★</span>
                      <span className="text-2xl font-bold">{normalizeRating(service.rating_avg).toFixed(1)}</span>
                    </div>
                    <span className="text-muted-foreground">({service.rating_count} reviews)</span>
                  </div>
                </div>
                
                {user && user.role === 'customer' && (
                  <Button onClick={() => setShowReviewForm(true)}>
                    Write a Review
                  </Button>
                )}
              </div>

              {service.reviews && service.reviews.length > 0 ? (
                <div className="space-y-4">
                  {service.reviews.map((review, idx) => (
                    <div key={idx} className="border-b border-border pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          <span className="font-semibold">{normalizeRating(review.rating).toFixed(1)}</span>
                        </div>
                        <span className="text-muted-foreground">•</span>
                        <span className="font-medium text-foreground">{review.customer_name}</span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">{new Date(review.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-muted-foreground">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
              )}

              {showReviewForm && (
                <div className="mt-6 pt-6 border-t border-border">
                  <ReviewForm
                    serviceId={serviceId}
                    onSuccess={() => {
                      setShowReviewForm(false);
                      fetchService();
                    }}
                    onCancel={() => setShowReviewForm(false)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Safety Tip */}
            <Card className="mt-8 bg-accent-yellow/10 border-accent-yellow/30">
              <CardContent className="p-6">
                <div className="flex gap-3">
                  <div className="text-2xl">🛡️</div>
                  <div>
                    <h4 className="font-semibold mb-1">Stay Safe</h4>
                    <p className="text-sm text-muted-foreground">
                      Always communicate through GoFella, verify credentials, and get quotes in writing before starting work.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      </div>
      
      {/* Gallery Lightbox */}
      {showGallery && (
        <MediaGallery 
          images={service.tradie?.photos || service.media_urls || []}
          videos={service.intro_video_url ? [service.intro_video_url] : []}
          initialIndex={galleryIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
      
      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowQuoteModal(false)}>
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Request a Quote</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Message</label>
                <Textarea
                  value={quoteData.message}
                  onChange={(e) => setQuoteData({ ...quoteData, message: e.target.value })}
                  placeholder="Describe what you need..."
                  rows={4}
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Preferred Date (Optional)</label>
                <Input
                  type="date"
                  value={quoteData.preferred_date}
                  onChange={(e) => setQuoteData({ ...quoteData, preferred_date: e.target.value })}
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setShowQuoteModal(false)} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={handleQuoteRequest} className="flex-1" disabled={!quoteData.message}>Send Request</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowReportModal(false)}>
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Report Listing</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Reason</label>
                <select
                  value={reportData.reason}
                  onChange={(e) => setReportData({ ...reportData, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-background"
                >
                  <option value="">Select reason...</option>
                  <option value="inappropriate">Inappropriate content</option>
                  <option value="spam">Spam or scam</option>
                  <option value="incorrect">Incorrect information</option>
                  <option value="duplicate">Duplicate listing</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Details</label>
                <Textarea
                  value={reportData.details}
                  onChange={(e) => setReportData({ ...reportData, details: e.target.value })}
                  placeholder="Please provide more details..."
                  rows={4}
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setShowReportModal(false)} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={handleReportSubmit} variant="destructive" className="flex-1" disabled={!reportData.reason}>Submit Report</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailPage;
